import { Container, Graphics } from 'pixi.js';
import { HoldIndicator } from './HoldIndicator';

/**
 * Visual hold-state chrome for a {@link ./SlotReel SlotReel}: the pink "HOLD"
 * pill above the reel and the highlight border drawn around the visible
 * window when the reel is held.
 *
 * <p>Owned by the reel; the reel drives visibility via {@link setHeld}.
 * Sizing is fixed at construction so the held-frame doesn't need to be
 * redrawn on every resize.
 */
export class SlotReelHoldUI extends Container {
  private readonly heldFrame: Graphics;
  private readonly indicator: HoldIndicator;

  constructor(symbolSize: number, visibleRows: number, accentColor: number = 0xff6bd6) {
    super();

    // Highlight frame — drawn INSIDE the reel cells rather than around
    // the outside. The themed slots paint a wood / metal frame ring
    // around the machine that covers anything outside the reel cells,
    // so an inset border is the only treatment that stays visible
    // regardless of frame. The inset is sized so it hugs the cell
    // edges without overlapping with the symbol art.
    this.heldFrame = new Graphics();
    this.heldFrame.visible = false;
    this.heldFrame
      .roundRect(
        -symbolSize / 2 + 6,
        6,
        symbolSize - 12,
        symbolSize * visibleRows - 12,
        10,
      )
      .stroke({ color: accentColor, alpha: 0.95, width: 4 });
    this.addChild(this.heldFrame);

    // HOLD pill sits ABOVE the slot machine's painted wood frame so
    // it's actually visible. Producer audit Phase 4: the prior y=-18
    // placed it inside the painted wooden top ring of the slot frame,
    // where the {@code borderSprite} in SlotDemoScene (50px thick
    // wood ring) drew over it — playtesters never saw any visual
    // confirmation of the hold even though the tap registered.
    // y=-72 clears the wood frame's top edge (BORDER_DISPLAY_RING_PX
    // = 50, accounting for the pill's 12px half-height + 10px margin)
    // so the pill renders in the transparent stage space above the
    // slot at any machine display scale.
    this.indicator = new HoldIndicator();
    this.indicator.x = 0;
    this.indicator.y = -72;
    this.addChild(this.indicator);
  }

  setHeld(held: boolean): void {
    if (held) {
      this.heldFrame.visible = true;
      this.indicator.show();
    } else {
      this.heldFrame.visible = false;
      this.indicator.hide();
    }
  }
}
